import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import MarketNotice from './components/MarketNotice';
import Home from './pages/Home';
import Features from './pages/Features';
import WorkflowFeatures from './pages/WorkflowFeatures';
import CaseManagement from './pages/CaseManagement';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Blogs from './pages/Blogs';
import Contact from './pages/Contact';
import BlogPost from './pages/BlogPost';
import ResponsibleAI from './pages/ResponsibleAI';
import DataPrivacy from './pages/DataPrivacy';
import PracticeAreas from './pages/PracticeAreas';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import Calculators from './pages/Calculators';
import { trackPageView } from './utils/analytics';
import { REGION } from './config/region';
import { ownMarket } from './config/market';
import './styles/globals.css';

function ScrollToTop() {
  const { pathname } = useLocation();
  const prevPathRef = useRef(pathname);
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = pathname;

    // Skip the scroll reset when navigating between profession tabs inside the
    // calculator — the user is just switching variants of the same page.
    const isCalcTabSwitch =
      prev !== pathname &&
      prev.startsWith('/calculators') &&
      pathname.startsWith('/calculators');
    if (!isCalcTabSwitch) window.scrollTo(0, 0);

    trackPageView(pathname);
  }, [pathname]);
  return null;
}

/* Pages this market does not have (region.json markets: its own omitRoutes,
   e.g. a research page in a market that does not sell research). Their URL
   goes to the homepage instead. */
const OMITTED = new Set(ownMarket(REGION).omitRoutes);
const page = (path: string, element: React.ReactElement) =>
  OMITTED.has(path) ? <Navigate to="/" replace /> : element;

const App: React.FC = () => {
  return (
    <Router>
      <ScrollToTop />
      <div className="app">
        <Navbar />
        <MarketNotice />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/features" element={page('/features', <Features />)} />
          <Route path="/workflow-features" element={page('/workflow-features', <WorkflowFeatures />)} />
          <Route path="/case-management" element={page('/case-management', <CaseManagement />)} />
          <Route path="/pricing" element={page('/pricing', <Pricing />)} />
          <Route path="/about" element={page('/about', <About />)} />
          <Route path="/blogs" element={page('/blogs', <Blogs />)} />
          <Route path="/blogs/:slug" element={<BlogPost />} />
          <Route path="/practice-areas" element={page('/practice-areas', <PracticeAreas />)} />
          <Route path="/calculators" element={page('/calculators', <Calculators />)} />
          <Route path="/calculators/:profession" element={<Calculators />} />
          <Route path="/responsible-ai" element={page('/responsible-ai', <ResponsibleAI />)} />
          <Route path="/data-privacy" element={page('/data-privacy', <DataPrivacy />)} />
          <Route path="/contact" element={page('/contact', <Contact />)} />
          <Route path="/privacy-policy" element={page('/privacy-policy', <PrivacyPolicy />)} />
          <Route path="/terms-and-conditions" element={page('/terms-and-conditions', <TermsAndConditions />)} />
        </Routes>
        <Footer />
      </div>
    </Router>
  );
};

export default App;
